import type { UseAuthData } from '../auth/useAuth'
import { useQuery } from '@tanstack/vue-query'
import KnowError from '~/exceptions/KnownError'
import queryClient, { queryKeys } from '~/queryClient'
import launchDarkly from '~/services-v2/launch-darkly/launch-darkly'

export function useFeatureFlag<T = unknown>(key: string, defaultValue = false as T, enableAdmin = false) {
  const queryKey = ['FEATURE_FLAG', key]

  const queryFn = async () => {
    const user = queryClient.getQueryData<UseAuthData>(queryKeys.AUTH.STATUS)
    if (!user)
      throw new KnowError('AUTH_NOT_READY')

    if (!user?.me?.parceiro?.id && !enableAdmin) {
      return false as T
    }

    try {
      await launchDarkly.waitForInitialization(20)

      await launchDarkly.identify({
        kind: 'user',
        key: String(user.me.id),
        name: user.me.nomeCompleto,
        email: user.me.email,
        partner_id: user.me.parceiro?.id,
        custom: {
          sfPlusLevel: user.solfacilplus.level,
          sfPlusCurrentScore: user.solfacilplus.currentScore,
          sfPlusGoalScore: user.solfacilplus.goScore,
          sfPlusNextTransitionDate: user.solfacilplus.nextTransitionDate,
        },
        enable_admin: enableAdmin,
      })

      const res = launchDarkly.variation(key, defaultValue)
      return res as T
    }
    catch (error) {
      console.warn(`Feature flag ${key} failed to load:`, error)
      return defaultValue as T
    }
  }

  return useQuery({
    queryKey,
    queryFn,
    staleTime: 1_000 * 60 * 2,
    refetchInterval: 1_000 * 60 * 2,
    retryDelay: 2_000,
    retry: 3,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}
